import React, { useState, useEffect, useRef } from 'react';
import { useDashboard } from '../../contexts/DashboardContext';
import { useAuth } from '../../contexts/AuthContext';
import CampaignsFilter from '../CampaignsFilter';
import VersionFilter from '../VersionFilter';
import TimePicker from '../TimePicker';
import { normalizeStartDate, normalizeEndDate } from '../../utils/dateUtils';
import { getPresetRange, getPreviousMonthRange } from '../../utils/datePresets';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Separator } from '../ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { CalendarIcon, PanelLeftClose } from 'lucide-react';
import { getNavSections } from './navSections';
import { cn } from '../../lib/utils';
import { ApiService } from '../../services/api';

// onNavigate вызывается после выбора раздела и после «Применить» — на мобильных
// это закрывает выдвижную панель, на десктопе проп не передаётся.
// onCollapse передаётся только на десктопе — показывает кнопку сворачивания панели
const Sidebar = ({ onNavigate, onCollapse, collapseButtonRef }) => {
  const { user, loading: authLoading } = useAuth();
  const {
    activeSection,
    setActiveSection,
    startDate,
    endDate,
    startTime,
    endTime,
    updateDates,
    updateTimes,
    selectedProject,
    updateSelectedProject,
    updateSelectedCampaigns,
    updateSelectedMediaSource,
    selectedPlatform,
    updateSelectedPlatform,
    selectedClientVersions,
    selectedInstallVersions,
    updateSelectedVersions,
    applyPlatformFees,
    updateApplyPlatformFees,
    includeOrganic,
    updateIncludeOrganic
  } = useDashboard();

  // Локальные состояния для временных дат
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  
  // Локальные состояния для временного времени
  const [tempStartTime, setTempStartTime] = useState({ hours: 0, minutes: 0 });
  const [tempEndTime, setTempEndTime] = useState({ hours: 23, minutes: 59 });
  
  // Временное состояние для проекта
  const [tempProject, setTempProject] = useState('all');
  
  // Список проектов из базы
  const [projects, setProjects] = useState([]);
  
  // Временное состояние для платформы
  const [tempPlatform, setTempPlatform] = useState('all');
  
  // Временные состояния фильтров версий и список доступных версий проекта
  const [tempClientVersions, setTempClientVersions] = useState([]);
  const [tempInstallVersions, setTempInstallVersions] = useState([]);
  const [versionOptions, setVersionOptions] = useState({ clientVersions: [], installVersions: [] });
  const [versionOptionsLoading, setVersionOptionsLoading] = useState(false);
  // Выставляется, когда пользователь сам сменил проект: после загрузки версий
  // нового проекта выбор версий, которых там нет, сбрасывается. Синхронизация
  // проекта из контекста/ссылки выбор не трогает.
  const pruneVersionsRef = useRef(false);

  const handleProjectChange = (project) => {
    pruneVersionsRef.current = true;
    setTempProject(project);
  };

  // Временное состояние для чекбокса коэффициентов
  const [tempApplyPlatformFees, setTempApplyPlatformFees] = useState(false);

  // Временное состояние для чекбокса органики в ROAS
  const [tempIncludeOrganic, setTempIncludeOrganic] = useState(true);
  
  // Состояние для фильтров кампаний и источников
  const [tempFilters, setTempFilters] = useState({
    mediaSource: 'all',
    campaigns: []
  });
  
  // Состояния для управления открытием календарей
  const [startDatePopoverOpen, setStartDatePopoverOpen] = useState(false);
  const [endDatePopoverOpen, setEndDatePopoverOpen] = useState(false);

  // Инициализация дат при первой загрузке
  useEffect(() => {
    if (!startDate && !endDate) {
      // Предварительный выбор в форме — прошлый календарный месяц (как дефолт дашборда)
      const { start, end } = getPreviousMonthRange();
      setTempStartDate(start);
      setTempEndDate(end);
    } else {
      // Нормализуем существующие даты
      setTempStartDate(startDate ? normalizeStartDate(startDate) : null);
      setTempEndDate(endDate ? normalizeEndDate(endDate) : null);
    }
  }, [startDate, endDate]);

  // Инициализация времени при первой загрузке
  useEffect(() => {
    setTempStartTime(startTime);
    setTempEndTime(endTime);
  }, [startTime, endTime]);

  // Инициализация проекта при первой загрузке
  useEffect(() => {
    setTempProject(selectedProject || 'all');
  }, [selectedProject]);

  // Инициализация платформы при первой загрузке
  useEffect(() => {
    setTempPlatform(selectedPlatform || 'all');
  }, [selectedPlatform]);

  // Версии следуют за состоянием дашборда (оно же приходит из ссылки)
  useEffect(() => {
    setTempClientVersions(selectedClientVersions || []);
  }, [selectedClientVersions]);

  useEffect(() => {
    setTempInstallVersions(selectedInstallVersions || []);
  }, [selectedInstallVersions]);

  // Чекбокс следует за состоянием дашборда (оно же приходит из ссылки)
  useEffect(() => {
    setTempApplyPlatformFees(applyPlatformFees);
  }, [applyPlatformFees]);

  // Чекбокс органики следует за состоянием дашборда (оно же приходит из ссылки)
  useEffect(() => {
    setTempIncludeOrganic(includeOrganic);
  }, [includeOrganic]);

  // Загрузка списка проектов из API
  useEffect(() => {
    // Не загружаем данные пока не авторизован пользователь
    if (authLoading || !user) {
      return;
    }

    const fetchProjects = async () => {
      try {
        const data = await ApiService.get('/projects');
        setProjects(data);
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      }
    };
    
    fetchProjects();
  }, [authLoading, user]);

  // Версии клиента/установки выбранного (ещё не применённого) проекта
  useEffect(() => {
    if (authLoading || !user) {
      return;
    }

    let cancelled = false;
    const fetchVersionOptions = async () => {
      setVersionOptionsLoading(true);
      try {
        const data = await ApiService.get('/filters/client-versions', {
          project: tempProject !== 'all' ? tempProject : undefined
        });
        if (!cancelled) {
          const clientVersions = data.clientVersions || [];
          const installVersions = data.installVersions || [];
          setVersionOptions({ clientVersions, installVersions });

          if (pruneVersionsRef.current) {
            pruneVersionsRef.current = false;
            const clientKnown = new Set(clientVersions.map(option => option.value));
            const installKnown = new Set(installVersions.map(option => option.value));
            setTempClientVersions(prev => prev.filter(version => clientKnown.has(version)));
            setTempInstallVersions(prev => prev.filter(version => installKnown.has(version)));
          }
        }
      } catch (error) {
        console.error('Failed to fetch client versions:', error);
        if (!cancelled) {
          setVersionOptions({ clientVersions: [], installVersions: [] });
        }
      } finally {
        if (!cancelled) {
          setVersionOptionsLoading(false);
        }
      }
    };

    fetchVersionOptions();
    return () => {
      cancelled = true;
    };
  }, [authLoading, user, tempProject]);

  const sections = getNavSections(user);

  const handleDateChange = (type, date) => {
    if (!date) {
      if (type === 'start') {
        setTempStartDate(null);
      } else {
        setTempEndDate(null);
      }
      return;
    }
    
    // ВСЕГДА нормализуем даты
    if (type === 'start') {
      setTempStartDate(normalizeStartDate(date));
      setStartDatePopoverOpen(false); // Закрываем попап после выбора даты
    } else {
      setTempEndDate(normalizeEndDate(date));
      setEndDatePopoverOpen(false); // Закрываем попап после выбора даты
    }
  };

  // Обработчик изменений фильтров кампаний и источников
  const handleFiltersChange = (filters) => {
    setTempFilters(filters);
  };

  // Обработчик изменения чекбокса — применяется сразу, без кнопки «Применить».
  // Контекст сам пишет localStorage, шлёт событие виджетам и обновляет ссылку.
  const handlePlatformFeesChange = (checked) => {
    setTempApplyPlatformFees(checked);
    updateApplyPlatformFees(checked);
  };

  // Обработчик изменения чекбокса органики — применяется сразу, без кнопки «Применить»
  const handleIncludeOrganicChange = (checked) => {
    setTempIncludeOrganic(checked);
    updateIncludeOrganic(checked);
  };

  const handleApply = () => {
    // Применяем даты
    updateDates(tempStartDate, tempEndDate);
    
    // Применяем время
    updateTimes(tempStartTime, tempEndTime);
    
    // Применяем проект
    updateSelectedProject(tempProject);
    
    // Применяем платформу
    updateSelectedPlatform(tempPlatform);
    
    // Применяем фильтры кампаний и источников
    updateSelectedMediaSource(tempFilters.mediaSource);
    updateSelectedCampaigns(tempFilters.campaigns);

    // Применяем версии клиента и установки
    updateSelectedVersions(tempClientVersions, tempInstallVersions);

    onNavigate?.();
  };

  // Пресеты дат
  const setPreset = (preset) => {
    const range = getPresetRange(preset);
    if (!range) return;
    const { start, end } = range;

    setTempStartDate(start);
    setTempEndDate(end);
    setTempStartTime({ hours: 0, minutes: 0 });
    setTempEndTime({ hours: 23, minutes: 59 });
  };

  // Сдвиг дат на день с нормализацией
  const shiftDates = (direction) => {
    if (!tempStartDate || !tempEndDate) return;

    const shift = direction === 'forward' ? 1 : -1;
    const newStart = new Date(tempStartDate);
    const newEnd = new Date(tempEndDate);
    
    newStart.setDate(newStart.getDate() + shift);
    newEnd.setDate(newEnd.getDate() + shift);
    
    // Нормализуем после сдвига
    setTempStartDate(normalizeStartDate(newStart));
    setTempEndDate(normalizeEndDate(newEnd));
    
    // Устанавливаем время от 00:00 до 23:59 при сдвиге дат
    setTempStartTime({ hours: 0, minutes: 0 });
    setTempEndTime({ hours: 23, minutes: 59 });
  };

  return (
    <div className="h-full flex flex-col min-h-0 overflow-hidden">
      {/* Прокручиваемая область фильтров: если высоты окна не хватает, скроллится она,
          а не выдавливает нижний блок с чекбоксом и кнопкой поверх остальных контролов */}
      <div className="flex-1 min-h-0 flex flex-col overflow-y-auto overflow-x-hidden">
        <div className="flex-shrink-0 p-4 space-y-6">
        {/* Навигация */}
        <div className="space-y-3">
          {onCollapse && (
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground px-3">Разделы</span>
              <Button
                ref={collapseButtonRef}
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={onCollapse}
                aria-expanded="true"
                title="Свернуть панель"
                aria-label="Свернуть панель"
              >
                <PanelLeftClose className="h-4 w-4" />
              </Button>
            </div>
          )}
          <div className="grid grid-cols-1 gap-1">
            {sections.map(section => {
              const IconComponent = section.icon;
              return (
                <Button
                  key={section.id}
                  variant={activeSection === section.id ? "secondary" : "ghost"}
                  size="sm"
                  className="h-9 text-sm justify-start px-3"
                  aria-current={activeSection === section.id ? 'page' : undefined}
                  onClick={() => {
                    setActiveSection(section.id);
                    onNavigate?.();
                  }}
                >
                  <IconComponent className="h-4 w-4 mr-2" />
                  <span>{section.name}</span>
                </Button>
              );
            })}
          </div>
        </div>

        <Separator />

        {/* Период */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium">Период</h3>
          
          {/* Кнопки пресетов */}
          <div className="grid grid-cols-3 gap-2">
            <Button variant="outline" size="sm" onClick={() => setPreset('today')} className="text-xs h-8">Сегодня</Button>
            <Button variant="outline" size="sm" onClick={() => setPreset('yesterday')} className="text-xs h-8">Вчера</Button>
            <Button variant="outline" size="sm" onClick={() => setPreset('currentMonth')} className="text-xs h-8">Тек. месяц</Button>
            <Button variant="outline" size="sm" onClick={() => setPreset('last3days')} className="text-xs h-8">3 дня</Button>
            <Button variant="outline" size="sm" onClick={() => setPreset('last7days')} className="text-xs h-8">7 дней</Button>
            <Button variant="outline" size="sm" onClick={() => setPreset('last30days')} className="text-xs h-8">30 дней</Button>
            <Button variant="outline" size="sm" onClick={() => setPreset('last90days')} className="text-xs h-8">90 дней</Button>
            <Button variant="outline" size="sm" onClick={() => setPreset('last180days')} className="text-xs h-8">180 дней</Button>
            <Button variant="outline" size="sm" onClick={() => setPreset('last360days')} className="text-xs h-8">360 дней</Button>
          </div>

          {/* Кнопки сдвига дат */}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => shiftDates('backward')} className="flex-1 text-xs h-8">← День</Button>
            <Button variant="outline" size="sm" onClick={() => shiftDates('forward')} className="flex-1 text-xs h-8">День →</Button>
          </div>
          
          {/* Поля дат и времени */}
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Popover open={startDatePopoverOpen} onOpenChange={setStartDatePopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "h-9 w-full justify-start text-left font-normal text-xs",
                      !tempStartDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-3 w-3" />
                    {tempStartDate ? (
                      tempStartDate.toLocaleDateString('ru-RU')
                    ) : (
                      "Начальная дата"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={tempStartDate}
                    month={tempStartDate || new Date()}
                    onSelect={(date) => handleDateChange('start', date)}
                    weekStartsOn={1}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <TimePicker
                value={tempStartTime}
                onChange={setTempStartTime}
                placeholder="00:00"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Popover open={endDatePopoverOpen} onOpenChange={setEndDatePopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "h-9 w-full justify-start text-left font-normal text-xs",
                      !tempEndDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-3 w-3" />
                    {tempEndDate ? (
                      tempEndDate.toLocaleDateString('ru-RU')
                    ) : (
                      "Конечная дата"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={tempEndDate}
                    month={tempEndDate || new Date()}
                    onSelect={(date) => handleDateChange('end', date)}
                    disabled={(date) => tempStartDate && date < tempStartDate}
                    weekStartsOn={1}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <TimePicker
                value={tempEndTime}
                onChange={setTempEndTime}
                placeholder="23:59"
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Фильтр по проекту */}
        <div className="space-y-3">
          <Select value={tempProject} onValueChange={handleProjectChange}>
            <SelectTrigger className="w-full h-9 text-xs">
              <SelectValue placeholder="Проект" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все проекты</SelectItem>
              {projects.map(project => (
                <SelectItem key={project} value={project}>
                  {project}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

          <Separator />
        </div>

        {/* Версии клиента и установки */}
        <div className="flex-shrink-0 px-4 pb-4 space-y-3">
          <VersionFilter
            title="Версия клиента"
            options={versionOptions.clientVersions}
            selected={tempClientVersions}
            onChange={setTempClientVersions}
            loading={versionOptionsLoading}
          />
          <VersionFilter
            title="Версия установки"
            options={versionOptions.installVersions}
            selected={tempInstallVersions}
            onChange={setTempInstallVersions}
            loading={versionOptionsLoading}
          />
          <Separator />
        </div>

        {/* Фильтр кампаний: забирает остаток высоты, но не сжимается ниже рабочего минимума */}
        <div className="flex-1 flex flex-col min-h-[260px] px-4 pb-4">
          <CampaignsFilter
            onFiltersChange={handleFiltersChange}
            selectedPlatform={tempPlatform}
            selectedProject={tempProject}
            tempPlatform={tempPlatform}
            onPlatformChange={setTempPlatform}
          />
        </div>
      </div>

      {/* Кнопка применить и чекбокс - зафиксированы внизу */}
      <div className="flex-shrink-0 p-4 border-t space-y-3 bg-sidebar">
        {/* Чекбокс для применения коэффициентов платформ.
            leading-snug вместо leading-none: в узком сайдбаре подпись переносится
            на несколько строк и при line-height:1 строки наезжали друг на друга */}
        <div className="flex items-start gap-2">
          <Checkbox
            id="apply-platform-fees"
            checked={tempApplyPlatformFees}
            onCheckedChange={handlePlatformFeesChange}
            className="mt-0.5 shrink-0"
          />
          <div className="min-w-0 flex-1 space-y-1">
            <label
              htmlFor="apply-platform-fees"
              className="block text-xs font-medium leading-snug break-words peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              Показывать чистый доход
            </label>
            <p className="text-xs leading-snug text-muted-foreground break-words">
              Инапы без VAT страны покупки и комиссии стора 30 %, реклама без изменений
            </p>
          </div>
        </div>
        
        {/* Галка учёта органики — только в разделе ROAS: в остальных разделах
            она ни на что не влияет и вводила бы в заблуждение */}
        {activeSection === 'roas' && (
          <div className="flex items-start gap-2">
            <Checkbox
              id="include-organic-roas"
              checked={tempIncludeOrganic}
              onCheckedChange={handleIncludeOrganicChange}
              className="mt-0.5 shrink-0"
            />
            <div className="min-w-0 flex-1 space-y-1">
              <label
                htmlFor="include-organic-roas"
                className="block text-xs font-medium leading-snug break-words peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                title="Снимите, чтобы считать ROAS только по платному трафику: строки source = Organic исключаются целиком — доход, инсталлы и CPI"
              >
                Учитывать органику в ROAS
              </label>
            </div>
          </div>
        )}

        <Button
          onClick={handleApply}
          className="w-full"
          disabled={!tempStartDate || !tempEndDate}
        >
          Применить
        </Button>
      </div>
    </div>
  );
};

export default Sidebar;
